import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Lock, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { OnboardingShell } from "@/components/shells/OnboardingShell";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";

// ONB-02 — Bank capture (editorial onboarding shell)
export default function BankCapturePage() {
  const navigate = useNavigate();
  const [routing, setRouting] = useState("");
  const [account, setAccount] = useState("");
  const [confirmAccount, setConfirmAccount] = useState("");
  const [bankName, setBankName] = useState("");
  const [accountType, setAccountType] = useState("checking");
  const [confirmOpen, setConfirmOpen] = useState(false);

  const routingValid = /^\d{9}$/.test(routing);
  const accountValid = account.length >= 4 && account.length <= 17 && /^\d+$/.test(account);
  const matches = account === confirmAccount && account.length > 0;
  const canSubmit = routingValid && accountValid && matches && bankName.trim().length > 0;

  const submit = () => {
    setConfirmOpen(false);
    toast.success("Bank account captured", { description: "Encrypted at rest. Final step: accept membership terms." });
    setTimeout(() => navigate("/onboarding"), 800);
  };

  return (
    <OnboardingShell currentStage="bank">
      <div className="eyebrow mb-3 text-brand-700">Step 4 of 6</div>
      <h1 className="font-brand text-[40px] lg:text-[44px] leading-[1.05] tracking-[-0.025em] text-ink font-bold">
        Where should we send your payouts?
      </h1>
      <p className="mt-5 text-body text-ink-muted leading-relaxed max-w-xl">
        We pay by ACH in US dollars. Enter the account where deal payouts should land &mdash; we encrypt everything before storage.
      </p>

      <form
        onSubmit={(e) => { e.preventDefault(); setConfirmOpen(true); }}
        data-testid="bank-form"
        className="mt-10 space-y-7 max-w-xl"
      >
        <Field label="Bank name" required>
          <Input
            value={bankName}
            onChange={(e) => setBankName(e.target.value)}
            placeholder="e.g., Chase, Bank of America, Capital One"
            required
            className="h-12 bg-surface"
          />
        </Field>

        <div className="grid sm:grid-cols-2 gap-5">
          <Field label="Routing number" required>
            <Input
              value={routing}
              onChange={(e) => setRouting(e.target.value.replace(/\D/g, "").slice(0, 9))}
              placeholder="9 digits" inputMode="numeric" maxLength={9}
              className="h-12 bg-surface font-mono tabular-nums" data-testid="routing-input" required
            />
            {routing.length > 0 && !routingValid && (
              <p className="mt-1.5 text-caption text-red-600">Routing number must be exactly 9 digits.</p>
            )}
          </Field>
          <Field label="Account type">
            <div className="flex gap-2">
              {["checking", "savings"].map((t) => (
                <button key={t} type="button" onClick={() => setAccountType(t)}
                        className={`flex-1 h-12 rounded-md border text-body-sm font-medium capitalize transition-all ${
                          accountType === t ? "bg-brand-50 border-brand-700 text-brand-800" : "border-line bg-surface text-ink-muted hover:border-ink"
                        }`}>
                  {t}
                </button>
              ))}
            </div>
          </Field>
        </div>

        <div className="grid sm:grid-cols-2 gap-5">
          <Field label="Account number" required>
            <Input type="password"
              value={account}
              onChange={(e) => setAccount(e.target.value.replace(/\D/g, "").slice(0, 17))}
              placeholder="•••••••••" inputMode="numeric" maxLength={17}
              className="h-12 bg-surface font-mono tabular-nums" data-testid="account-input" required
            />
          </Field>
          <Field label="Confirm account number" required>
            <Input type="password"
              value={confirmAccount}
              onChange={(e) => setConfirmAccount(e.target.value.replace(/\D/g, "").slice(0, 17))}
              placeholder="•••••••••" inputMode="numeric" maxLength={17}
              className="h-12 bg-surface font-mono tabular-nums" data-testid="confirm-account-input" required
            />
            {confirmAccount.length > 0 && !matches && (
              <p className="mt-1.5 text-caption text-red-600">Account numbers don&rsquo;t match.</p>
            )}
          </Field>
        </div>

        <div className="bg-surface-alt border-l-2 border-brand-700 rounded-r-md p-4 flex items-start gap-3">
          <ShieldCheck className="size-4 text-brand-700 mt-0.5 shrink-0" />
          <div className="text-caption text-ink-muted leading-relaxed">
            Your account number is encrypted with AWS KMS before being written to disk. Only Payments Overseers can decrypt it, and every decryption is logged with their admin ID, IP, and timestamp.
          </div>
        </div>

        <div className="flex justify-between items-center pt-2 border-t border-line">
          <Link to="/onboarding" className="text-body-sm text-ink-muted hover:text-ink">Save &amp; finish later</Link>
          <Button type="submit" disabled={!canSubmit} data-testid="bank-submit-btn"
                  className="bg-brand-700 hover:bg-brand-800 text-white h-12 px-7 text-body-sm font-semibold">
            <Lock className="size-4 mr-1.5" /> Encrypt &amp; save
          </Button>
        </div>
      </form>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm bank details</DialogTitle>
            <DialogDescription>
              Review carefully &mdash; payouts will go to this exact account. After saving, only the last 4 digits will be visible to you.
            </DialogDescription>
          </DialogHeader>
          <dl className="bg-surface-alt border border-line rounded-md p-4 space-y-2 text-body-sm">
            <Row label="Bank" value={bankName} />
            <Row label="Routing" value={routing} mono />
            <Row label="Account" value={`••••${account.slice(-4)}`} mono />
            <Row label="Type" value={accountType} />
          </dl>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>Edit</Button>
            <Button onClick={submit} className="bg-brand-700 hover:bg-brand-800 text-white">Confirm and save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </OnboardingShell>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <Label className="text-eyebrow uppercase text-ink-muted mb-1.5 block">
        {label} {required && <span className="text-brand-700 normal-case">*</span>}
      </Label>
      {children}
    </div>
  );
}

function Row({ label, value, mono = false }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-ink-subtle">{label}</dt>
      <dd className={`text-ink font-medium ${mono ? "font-mono text-code" : ""}`}>{value}</dd>
    </div>
  );
}
